import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Card, CardContent, Chip, TextField,
  CircularProgress, IconButton, Tooltip, Snackbar, Alert,
  InputAdornment, useMediaQuery, useTheme,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Person as PersonIcon, Phone as PhoneIcon, Email as EmailIcon,
  LocationOn as LocationIcon, Schedule as ScheduleIcon,
  Refresh as RefreshIcon, Search as SearchIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { apiService } from '../../services/api';

interface ClientEntry {
  client: {
    _id: string;
    name: string;
    email: string;
    phoneNumber?: string;
    city?: string;
    address?: string;
    code?: string;
  };
  appointments: Array<{
    _id: string;
    scheduledDate: string;
    timeSlot: string;
    status: string;
    address: { street: string; city: string };
  }>;
}

const statusConfig: Record<string, { label: string; color: any }> = {
  scheduled: { label: 'Programada', color: 'info' },
  in_progress: { label: 'En Progreso', color: 'warning' },
  completed: { label: 'Completada', color: 'success' },
  cancelled: { label: 'Cancelada', color: 'error' },
  no_show: { label: 'No Show', color: 'default' },
};

function formatDate(d: string) {
  return new Date(d).toLocaleDateString('es-DO', { weekday: 'short', day: '2-digit', month: 'short' });
}

const TechnicianClients: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [clientEntries, setClientEntries] = useState<ClientEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.getMyClients();
      setClientEntries(res.data || []);
    } catch (e) {
      console.error('Error:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  const filtered = clientEntries.filter(entry => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      entry.client.name?.toLowerCase().includes(q) ||
      entry.client.email?.toLowerCase().includes(q) ||
      entry.client.phoneNumber?.includes(q) ||
      entry.client.city?.toLowerCase().includes(q) ||
      entry.client.code?.toLowerCase().includes(q)
    );
  });

  const pendingCount = clientEntries.filter(e =>
    e.appointments.some(a => a.status === 'scheduled' || a.status === 'in_progress')
  ).length;

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight={600}>Mis Clientes</Typography>
          <Typography variant="body2" color="text.secondary">
            {clientEntries.length} cliente(s) • {pendingCount} con citas pendientes
          </Typography>
        </Box>
        <Tooltip title="Actualizar">
          <IconButton onClick={fetchClients} color="primary"><RefreshIcon /></IconButton>
        </Tooltip>
      </Box>

      {/* Search */}
      <TextField
        fullWidth
        size="small"
        placeholder="Buscar por nombre, email, teléfono..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
        }}
      />

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>
      ) : filtered.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <PersonIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">
            {search ? 'No se encontraron clientes' : 'No tienes clientes asignados aún'}
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {filtered.map(({ client, appointments }) => {
            const hasPending = appointments.some(a => a.status === 'scheduled' || a.status === 'in_progress');
            const nextAppt = appointments.find(a => a.status === 'scheduled' || a.status === 'in_progress');
            const completedCount = appointments.filter(a => a.status === 'completed').length;

            return (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={client._id}>
                <Card
                  variant="outlined"
                  sx={{
                    height: '100%',
                    borderLeft: hasPending ? '4px solid' : undefined,
                    borderLeftColor: hasPending ? 'primary.main' : undefined,
                  }}
                >
                  <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                    {/* Client name and code */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon color="primary" />
                        <Box>
                          <Typography variant="subtitle1" fontWeight={600} lineHeight={1.2}>
                            {client.name}
                          </Typography>
                          {client.code && (
                            <Typography variant="caption" color="text.secondary">{client.code}</Typography>
                          )}
                        </Box>
                      </Box>
                      {hasPending && <Chip label="Pendiente" size="small" color="warning" />}
                    </Box>

                    {/* Contact info - clickable */}
                    {client.phoneNumber && (
                      <Box
                        component="a"
                        href={`tel:${client.phoneNumber}`}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 1, mb: 0.5,
                          textDecoration: 'none', color: 'success.main',
                          p: 1, borderRadius: 1, backgroundColor: 'success.50',
                          '&:active': { backgroundColor: 'success.100' },
                        }}
                      >
                        <PhoneIcon fontSize="small" />
                        <Typography variant="body2" fontWeight={500}>{client.phoneNumber}</Typography>
                      </Box>
                    )}

                    {client.email && (
                      <Box
                        component="a"
                        href={`mailto:${client.email}`}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 1, mb: 0.5,
                          textDecoration: 'none', color: 'text.secondary',
                          p: 0.5, pl: 1,
                        }}
                      >
                        <EmailIcon fontSize="small" />
                        <Typography variant="body2" noWrap>{client.email}</Typography>
                      </Box>
                    )}

                    {(client.city || client.address) && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, pl: 1 }}>
                        <LocationIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {client.address ? `${client.address}, ${client.city}` : client.city}
                        </Typography>
                      </Box>
                    )}

                    {/* Appointment summary */}
                    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {completedCount > 0 && (
                          <Chip
                            icon={<CheckIcon />}
                            label={`${completedCount} completada(s)`}
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        )}
                        {nextAppt && (
                          <Chip
                            icon={<ScheduleIcon />}
                            label={`${formatDate(nextAppt.scheduledDate)} ${nextAppt.timeSlot}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        )}
                      </Box>

                      {/* Next appointment address */}
                      {nextAppt && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                          <LocationIcon fontSize="small" color="primary" />
                          <Typography variant="caption" color="primary">
                            {nextAppt.address.street}, {nextAppt.address.city}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
};

export default TechnicianClients;
